import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "./hooks/useAuth";
import { LanguageProvider } from "./contexts/LanguageContext";
import { SubscriptionProvider } from "./contexts/SubscriptionContext";
import { ExamSessionProvider } from "./contexts/ExamSessionContext";
import { AuthGate } from "./components/AuthGate";
import { useSessionManager } from "./hooks/useSessionManager";

const SessionGuard = () => {
  useSessionManager();
  return null;
};
import { ProtectedRoute } from "./components/ProtectedRoute";
import { SubscriptionRoute } from "./components/SubscriptionRoute";
import { AdminRoute } from "./components/AdminRoute";
import { OwnerRoute } from "./components/OwnerRoute";
import { InstitutionAdminRoute } from "./components/InstitutionAdminRoute";
import { InstitutionRoute } from "./components/InstitutionRoute";
import { VisitTracker } from "./components/VisitTracker";
import { NavigationBreadcrumbs } from "./components/NavigationBreadcrumbs";
import { UpdateOverlay } from "./components/UpdateOverlay";
import { UpdateBanner } from "./components/UpdateBanner";
import { UpdateDebugPanel } from "./components/UpdateDebugPanel";
import { PrivacyAcknowledgmentBanner } from "./components/PrivacyAcknowledgmentBanner";

import { NativeConnectionError } from "./components/NativeConnectionError";
import { MobileOptimizations } from "./components/MobileOptimizations";
import { OverlayGuard } from "./components/OverlayGuard";
import { DeviceDetectionToast } from "./components/DeviceDetectionToast";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { TextScaleInitializer } from "./components/TextScaleInitializer";
import { usePresenceHeartbeat } from "./hooks/usePresenceHeartbeat";
import { useAppStatePreservation } from "./hooks/useAppStatePreservation";
import { useAppStateRestoration } from "./hooks/useAppStateRestoration";
import { useAutoTheme } from "./hooks/useAutoTheme";
import { useInactivityCacheRefresh } from "./hooks/useInactivityCacheRefresh";
import { useSourceTracking } from "./hooks/useSourceTracking";

import DevOverlay from "./components/DevOverlay";
import { GlobalNavHeader } from "./components/GlobalNavHeader";

// Global error handler
window.addEventListener('error', (event) => {
  console.error('🔴 [Global Error Handler] Uncaught error:', event.error);
  console.error('🔴 [Global Error Handler] Stack:', event.error?.stack);
  console.error('🔴 [Global Error Handler] Message:', event.message);
  console.error('🔴 [Global Error Handler] Filename:', event.filename);
  console.error('🔴 [Global Error Handler] Line:Column', `${event.lineno}:${event.colno}`);
});

window.addEventListener('unhandledrejection', (event) => {
  console.error('🔴 [Global Error Handler] Unhandled promise rejection:', event.reason);
  console.error('🔴 [Global Error Handler] Promise:', event.promise);
});

import { Suspense } from "react";
import { lazyWithRetry, installPreloadErrorHandler } from "@/lib/lazy-with-retry";

installPreloadErrorHandler();
import { Loader2 } from "lucide-react";

// Eager: landing + auth (critical path)
import Index from "./pages/Index";
import Login from "./pages/auth/Login";
import Register from "./pages/auth/Register";
const InstallPrompt = lazyWithRetry(() => import("./pages/auth/InstallPrompt"));
import StudentAccess from "./pages/auth/StudentAccess";

// Lazy: everything else
const OnboardingQuiz = lazyWithRetry(() => import("./pages/OnboardingQuiz"));
const Dashboard = lazyWithRetry(() => import("./pages/Dashboard"));
const ProgressPage = lazyWithRetry(() => import("./pages/Progress"));
const Settings = lazyWithRetry(() => import("./pages/Settings"));
const Study = lazyWithRetry(() => import("./pages/Study"));
const Quiz = lazyWithRetry(() => import("./pages/Quiz"));
const QuizLevel = lazyWithRetry(() => import("./pages/QuizLevel"));
const ExamSimulation = lazyWithRetry(() => import("./pages/ExamSimulation"));
const RealExamMode = lazyWithRetry(() => import("./pages/RealExamMode"));
const RefreshApp = lazyWithRetry(() => import("./pages/RefreshApp"));
const ReviewBank = lazyWithRetry(() => import("./pages/ReviewBank"));
const VisualAnatomyPractice = lazyWithRetry(() => import("./pages/VisualAnatomyPractice"));
const AnatomyHub = lazyWithRetry(() => import("./pages/AnatomyHub"));
const AnatomyViewer = lazyWithRetry(() => import("./pages/AnatomyViewer"));
const AnatomyLearn = lazyWithRetry(() => import("./pages/AnatomyLearn"));
const ForgotPassword = lazyWithRetry(() => import("./pages/auth/ForgotPassword"));
const ResetPassword = lazyWithRetry(() => import("./pages/auth/ResetPassword"));
const SetPassword = lazyWithRetry(() => import("./pages/auth/SetPassword"));
const VerifyEmail = lazyWithRetry(() => import("./pages/auth/VerifyEmail"));
const AuthCallback = lazyWithRetry(() => import("./pages/auth/AuthCallback"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const Unsubscribe = lazyWithRetry(() => import("./pages/Unsubscribe"));
const SharedStatsView = lazyWithRetry(() => import("./pages/SharedStatsView"));
const FAQ = lazyWithRetry(() => import("./pages/FAQ"));
const MblexPracticeTest = lazyWithRetry(() => import("./pages/MblexPracticeTest"));
const ExamenMblexEspanol = lazyWithRetry(() => import("./pages/ExamenMblexEspanol"));
const MblexStudyGuide = lazyWithRetry(() => import("./pages/MblexStudyGuide"));
const SchoolsLanding = lazyWithRetry(() => import("./pages/SchoolsLanding"));
const MassageTherapyExamPrep = lazyWithRetry(() => import("./pages/MassageTherapyExamPrep"));
const MblexExamSimulator = lazyWithRetry(() => import("./pages/MblexExamSimulator"));
const MblexQuestions = lazyWithRetry(() => import("./pages/MblexQuestions"));
const Privacy = lazyWithRetry(() => import("./pages/legal/Privacy"));
const DeleteAccount = lazyWithRetry(() => import("./pages/legal/DeleteAccount"));
const Terms = lazyWithRetry(() => import("./pages/legal/Terms"));
const Refunds = lazyWithRetry(() => import("./pages/legal/Refunds"));
const Support = lazyWithRetry(() => import("./pages/legal/Support"));
const Messages = lazyWithRetry(() => import("./pages/Messages"));
const InstitutionDashboard = lazyWithRetry(() => import("./pages/InstitutionDashboard"));
const SchoolPicker = lazyWithRetry(() => import("./pages/SchoolPicker"));
const SchoolDashboard = lazyWithRetry(() => import("./pages/SchoolDashboard"));
const InstitutionActivation = lazyWithRetry(() => import("./pages/InstitutionActivation"));
const AccessActivated = lazyWithRetry(() => import("./pages/access/AccessActivated"));

// Admin (rarely accessed, biggest savings)
const AdminQuestions = lazyWithRetry(() => import("./pages/AdminQuestions"));
const AdminQuestionEditor = lazyWithRetry(() => import("./pages/AdminQuestionEditor"));
const AdminUsers = lazyWithRetry(() => import("./pages/AdminUsers"));
const AdminContentAnalysis = lazyWithRetry(() => import("./pages/AdminContentAnalysis"));
const AdminQuestionReview = lazyWithRetry(() => import("./pages/AdminQuestionReview"));
const AdminStatistics = lazyWithRetry(() => import("./pages/AdminStatistics"));
const AdminVisualAnatomy = lazyWithRetry(() => import("./pages/AdminVisualAnatomy"));
const AdminTopStudents = lazyWithRetry(() => import("./pages/AdminTopStudents"));
const AdminTherapyApplicants = lazyWithRetry(() => import("./pages/AdminTherapyApplicants"));
const AdminRecruitmentSettings = lazyWithRetry(() => import("./pages/AdminRecruitmentSettings"));
const AdminTranslationCoverage = lazyWithRetry(() => import("./pages/AdminTranslationCoverage"));
const AdminMBLExReformulation = lazyWithRetry(() => import("./pages/AdminMBLExReformulation"));
const AdminBlueprintDiagnostics = lazyWithRetry(() => import("./pages/AdminBlueprintDiagnostics"));
const AdminBlueprintValidation = lazyWithRetry(() => import("./pages/AdminBlueprintValidation"));
const AdminExportQuestions = lazyWithRetry(() => import("./pages/AdminExportQuestions"));
const AdminDuplicateQuarantine = lazyWithRetry(() => import("./pages/AdminDuplicateQuarantine"));
const AdminReworkBank = lazyWithRetry(() => import("./pages/AdminReworkBank"));
const AdminUserStats = lazyWithRetry(() => import("./pages/AdminUserStats"));
const AdminPedagogyAudit = lazyWithRetry(() => import("./pages/AdminPedagogyAudit"));
const AdminPedagogyReviewBank = lazyWithRetry(() => import("./pages/AdminPedagogyReviewBank"));
const AdminCohorts = lazyWithRetry(() => import("./pages/AdminCohorts"));
const AdminInstitutionRequests = lazyWithRetry(() => import("./pages/AdminInstitutionRequests"));
const AdminQualityAudit = lazyWithRetry(() => import("./pages/AdminQualityAudit"));
const AdminAcademicIntelligence = lazyWithRetry(() => import("./pages/AdminAcademicIntelligence"));
const AdminReadinessCalibration = lazyWithRetry(() => import("./pages/AdminReadinessCalibration"));
const AdminEngagementInsights = lazyWithRetry(() => import("./pages/AdminEngagementInsights"));
const AdminReviews = lazyWithRetry(() => import("./pages/AdminReviews"));


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes (formerly cacheTime)
      retry: 1,
      refetchOnWindowFocus: false, // Avoid unnecessary refetches during development
    },
  },
});

const AppContent = () => {
  useAppStatePreservation();
  useAppStateRestoration();
  useAutoTheme(); // Apply theme globally (light/dark/auto) from app root
  useInactivityCacheRefresh(); // Safe cache refresh after 12h inactivity (keeps session)
  usePresenceHeartbeat(); // Update last_activity_at every 30s for online/offline indicator
  useSourceTracking(); // Fase 0: capture access source (pwa/email/browser) and emit app_opened once per tab

  return (
    <>
      <TextScaleInitializer />
      <MobileOptimizations />
      <OverlayGuard />

      <DeviceDetectionToast />
      <NativeConnectionError />
      
      <VisitTracker />
      <NavigationBreadcrumbs />
      <GlobalNavHeader />
      <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>}>
      <Routes>
        <Route path="/" element={<Index />} />
            <Route path="/auth/register" element={<Register />} />
            <Route path="/auth/install-prompt" element={<InstallPrompt />} />
            <Route
              path="/onboarding"
              element={
                <ProtectedRoute>
                  <OnboardingQuiz />
                </ProtectedRoute>
              }
            />
            <Route path="/auth/login" element={<Login />} />
            <Route path="/auth/student-access" element={<StudentAccess />} />
            <Route path="/auth/forgot" element={<ForgotPassword />} />
            <Route path="/auth/reset" element={<ResetPassword />} />
            <Route path="/auth/set-password" element={<SetPassword />} />
            <Route path="/auth/verify-email" element={<VerifyEmail />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route
              path="/access/activated"
              element={
                <ProtectedRoute>
                  <AccessActivated />
                </ProtectedRoute>
              }
            />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/progress"
              element={
                <ProtectedRoute>
                  <ProgressPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/study"
              element={
                <SubscriptionRoute>
                  <Study />
                </SubscriptionRoute>
              }
            />
            <Route
              path="/quiz"
              element={
                <SubscriptionRoute>
                  <Quiz />
                </SubscriptionRoute>
              }
            />
            <Route
              path="/quiz/:category/:level"
              element={
                <SubscriptionRoute>
                  <QuizLevel />
                </SubscriptionRoute>
              }
            />
            <Route
              path="/admin-questions"
              element={
                <AdminRoute>
                  <AdminQuestions />
                </AdminRoute>
              }
            />
            <Route
              path="/admin-reviews"
              element={
                <AdminRoute>
                  <AdminReviews />
                </AdminRoute>
              }
            />
            <Route
              path="/admin-users"
              element={
                <AdminRoute>
                  <AdminUsers />
                </AdminRoute>
              }
            />
            <Route
              path="/admin-content-analysis"
              element={
                <AdminRoute>
                  <AdminContentAnalysis />
                </AdminRoute>
              }
            />
            <Route
              path="/admin-question-review"
              element={
                <AdminRoute>
                  <AdminQuestionReview />
                </AdminRoute>
              }
            />
            <Route
              path="/admin-statistics"
              element={
                <AdminRoute>
                  <AdminStatistics />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/academic-intelligence"
              element={
                <OwnerRoute>
                  <AdminAcademicIntelligence />
                </OwnerRoute>
              }
            />
            <Route
              path="/admin/readiness-calibration"
              element={
                <OwnerRoute>
                  <AdminReadinessCalibration />
                </OwnerRoute>
              }
            />
            <Route
              path="/admin/engagement-insights"
              element={
                <AdminRoute>
                  <AdminEngagementInsights />
                </AdminRoute>
              }
            />

            <Route
              path="/admin/question-editor"
              element={
                <AdminRoute>
                  <AdminQuestionEditor />
                </AdminRoute>
              }
            />
            <Route
              path="/admin-visual-anatomy"
              element={
                <AdminRoute>
                  <AdminVisualAnatomy />
                </AdminRoute>
              }
            />
            <Route
              path="/admin-top-students"
              element={
                <AdminRoute>
                  <AdminTopStudents />
                </AdminRoute>
              }
            />
            <Route
              path="/admin-therapy-applicants"
              element={
                <AdminRoute>
                  <AdminTherapyApplicants />
                </AdminRoute>
              }
            />
            <Route
              path="/admin-recruitment-settings"
              element={
                <AdminRoute>
                  <AdminRecruitmentSettings />
                </AdminRoute>
              }
            />
            <Route
              path="/admin-translation-coverage"
              element={
                <AdminRoute>
                  <AdminTranslationCoverage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin-mblex-reformulation"
              element={
                <AdminRoute>
                  <AdminMBLExReformulation />
                </AdminRoute>
              }
            />
            <Route
              path="/admin-blueprint-diagnostics"
              element={
                <AdminRoute>
                  <AdminBlueprintDiagnostics />
                </AdminRoute>
              }
            />
            <Route
              path="/admin-blueprint-validation"
              element={
                <AdminRoute>
                  <AdminBlueprintValidation />
                </AdminRoute>
              }
            />
            <Route
              path="/admin-duplicate-quarantine"
              element={
                <AdminRoute>
                  <AdminDuplicateQuarantine />
                </AdminRoute>
              }
            />
            <Route
              path="/admin-rework-bank"
              element={
                <AdminRoute>
                  <AdminReworkBank />
                </AdminRoute>
              }
            />
            <Route
              path="/admin-export-questions"
              element={
                <AdminRoute>
                  <AdminExportQuestions />
                </AdminRoute>
              }
            />
            <Route
              path="/admin-pedagogy-audit"
              element={
                <AdminRoute>
                  <AdminPedagogyAudit />
                </AdminRoute>
              }
            />
            <Route
              path="/admin-pedagogy-review-bank"
              element={
                <AdminRoute>
                  <AdminPedagogyReviewBank />
                </AdminRoute>
              }
            />
            <Route
              path="/exam-simulation"
              element={
                <SubscriptionRoute>
                  <ExamSimulation />
                </SubscriptionRoute>
              }
            />
            <Route
              path="/real-exam"
              element={
                <SubscriptionRoute>
                  <RealExamMode />
                </SubscriptionRoute>
              }
            />
            <Route path="/refresh-app" element={<RefreshApp />} />
            <Route
              path="/review-bank"
              element={
                <SubscriptionRoute>
                  <ReviewBank />
                </SubscriptionRoute>
              }
            />
            <Route
              path="/visual-anatomy"
              element={
                <SubscriptionRoute>
                  <VisualAnatomyPractice />
                </SubscriptionRoute>
              }
            />
            <Route path="/anatomy" element={<AdminRoute><AnatomyHub /></AdminRoute>} />
            <Route path="/anatomy/:regionSlug" element={<AdminRoute><AnatomyViewer /></AdminRoute>} />
            <Route path="/anatomy/:regionSlug/learn" element={<AdminRoute><AnatomyLearn /></AdminRoute>} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/schools" element={<SchoolsLanding />} />
<Route path="/mblex-practice-test" element={<MblexPracticeTest />} />
            <Route path="/examen-mblex-espanol" element={<ExamenMblexEspanol />} />
            <Route path="/mblex-study-guide" element={<MblexStudyGuide />} />
            <Route path="/massage-therapy-exam-prep" element={<MassageTherapyExamPrep />} />
            <Route path="/mblex-exam-simulator" element={<MblexExamSimulator />} />
            <Route path="/mblex-questions" element={<MblexQuestions />} />
            {/* Legal routes — canonical namespace is /legal/*. Every legacy or
                short alias 301-style redirects so a single document version is
                ever live (previously /privacy-policy served a second, divergent
                English-only privacy text). */}
            <Route path="/legal/privacy" element={<Privacy />} />
            <Route path="/legal/terms" element={<Terms />} />
            <Route path="/legal/refunds" element={<Refunds />} />
            <Route path="/legal/support" element={<Support />} />
            <Route path="/legal/delete-account" element={<DeleteAccount />} />
            <Route path="/privacy" element={<Navigate to="/legal/privacy" replace />} />
            <Route path="/privacy-policy" element={<Navigate to="/legal/privacy" replace />} />
            <Route path="/terms" element={<Navigate to="/legal/terms" replace />} />
            <Route path="/refunds" element={<Navigate to="/legal/refunds" replace />} />
            <Route path="/support" element={<Navigate to="/legal/support" replace />} />
            <Route path="/delete-account" element={<Navigate to="/legal/delete-account" replace />} />
            <Route
              path="/admin/users/:userId/stats"
              element={
                <AdminRoute>
                  <AdminUserStats />
                </AdminRoute>
              }
            />
            <Route path="/share/stats/:token" element={<SharedStatsView />} />
            <Route
              path="/institution"
              element={
                <InstitutionAdminRoute>
                  <SchoolPicker />
                </InstitutionAdminRoute>
              }
            />
            <Route
              path="/institution/school/:schoolId"
              element={
                <InstitutionAdminRoute>
                  <SchoolDashboard />
                </InstitutionAdminRoute>
              }
            />
            <Route
              path="/institution/activate/:schoolId"
              element={
                <InstitutionAdminRoute>
                  <InstitutionActivation />
                </InstitutionAdminRoute>
              }
            />

            <Route
              path="/institution/:cohortId"
              element={
                <InstitutionRoute>
                  <InstitutionDashboard />
                </InstitutionRoute>
              }
            />
            <Route
              path="/admin-cohorts"
              element={
                <InstitutionAdminRoute>
                  <AdminCohorts />
                </InstitutionAdminRoute>
              }
            />
            <Route
              path="/admin-institution-requests"
              element={
                <AdminRoute>
                  <AdminInstitutionRequests />
                </AdminRoute>
              }
            />

            <Route
              path="/messages"
              element={
                <ProtectedRoute>
                  <Messages />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin-quality-audit"
              element={
                <AdminRoute>
                  <AdminQualityAudit />
                </AdminRoute>
              }
            />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
            </Routes>
      </Suspense>

            {/* Global update UI — single source for the whole app */}
            <UpdateOverlay />
            <UpdateBanner />
            <UpdateDebugPanel />
            <PrivacyAcknowledgmentBanner />


            {/* Development Tools - Only visible in dev mode */}
            <DevOverlay />
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <LanguageProvider>
            <AuthProvider>
              <ExamSessionProvider>
                <SubscriptionProvider>
                  <ErrorBoundary>
                    <AuthGate>
                      <SessionGuard />
                      <AppContent />
                    </AuthGate>
                  </ErrorBoundary>
                </SubscriptionProvider>
              </ExamSessionProvider>
            </AuthProvider>
          </LanguageProvider>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
